import { useRef } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import {
  addDaysToYmd, fmtFrLong, isValidYmd, todayYmd,
  addMonthsToYm, fmtFrMonth, isValidYm, currentYm,
} from '../lib/date.js';

interface Props {
  value: string;            // 'day' → YYYY-MM-DD ; 'month' → YYYY-MM
  onChange: (next: string) => void;
  /** 'day' (default) picks a single day; 'month' picks a month + year. */
  mode?: 'day' | 'month';
  /** Day mode only. When provided, prev/next skip days for which this
   *  returns true (weekend + holidays in Saisie), and "Aujourd'hui"
   *  snaps back to the most recent working day if today is non-working. */
  isNonWorking?: (ymd: string) => boolean;
}

// Unified date navigator (Phase: fix-date-pickers). The label + calendar
// icon are clickable and call the native input's showPicker(), because a
// transparent overlaid <input type=date/month> does NOT reliably open the
// picker on click in Chrome/Safari — only its indicator or showPicker()
// does. Used by /entry and /dashboard (day) and /monthly (month).
export function DateNavigator({ value, onChange, mode = 'day', isNonWorking }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isMonth = mode === 'month';

  const safe = isMonth
    ? (isValidYm(value) ? value : currentYm())
    : (isValidYmd(value) ? value : todayYmd());
  const todayVal = isMonth ? currentYm() : todayYmd();
  const label = isMonth ? fmtFrMonth(safe) : fmtFrLong(safe);

  // Step one unit, then (day mode, Saisie) keep going in the same
  // direction over non-working days so prev/next land on a work day.
  // Guard caps the skip so a misconfigured predicate can't loop forever.
  const step = (n: number) => {
    if (isMonth) { onChange(addMonthsToYm(safe, n)); return; }
    let next = addDaysToYmd(safe, n);
    if (isNonWorking) {
      for (let i = 0; i < 31 && isNonWorking(next); i++) next = addDaysToYmd(next, n);
    }
    onChange(next);
  };

  // "Aujourd'hui": today, snapped back to the latest working day when
  // today itself is a weekend/holiday (Saisie shows working days only).
  const goToday = () => {
    if (isMonth || !isNonWorking) { onChange(todayVal); return; }
    let t = todayVal;
    for (let i = 0; i < 31 && isNonWorking(t); i++) t = addDaysToYmd(t, -1);
    onChange(t);
  };

  const openPicker = () => {
    const el = inputRef.current;
    if (!el) return;
    // showPicker() is the reliable cross-browser way to open the popover
    // from a click; fall back to focus() where it's unavailable.
    if (typeof el.showPicker === 'function') {
      try { el.showPicker(); return; } catch { /* fall through */ }
    }
    el.focus();
  };

  return (
    <div className="date-nav" data-testid="date-nav" data-mode={mode}>
      <button
        type="button"
        className="icon-button"
        onClick={() => step(-1)}
        aria-label={isMonth ? 'Mois précédent' : 'Jour précédent'}
        data-testid="date-nav-prev"
      >
        <ChevronLeft aria-hidden="true" />
      </button>

      <div
        className="date-nav-display"
        role="button"
        tabIndex={0}
        onClick={openPicker}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPicker(); } }}
        aria-label={isMonth ? 'Choisir un mois' : 'Choisir une date'}
        data-testid="date-nav-open"
      >
        <CalendarDays aria-hidden="true" />
        <span className="date-nav-long">{label}</span>
        <input
          ref={inputRef}
          type={isMonth ? 'month' : 'date'}
          value={safe}
          onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
          className="date-nav-input"
          aria-label={isMonth ? 'Choisir un mois' : 'Choisir une date'}
          data-testid="date-nav-input"
          tabIndex={-1}
        />
      </div>

      <button
        type="button"
        className="icon-button"
        onClick={() => step(1)}
        aria-label={isMonth ? 'Mois suivant' : 'Jour suivant'}
        data-testid="date-nav-next"
      >
        <ChevronRight aria-hidden="true" />
      </button>

      {safe !== todayVal && (
        <button
          type="button"
          className="btn btn-primary"
          onClick={goToday}
          data-testid="date-nav-today"
        >
          {isMonth ? 'Ce mois-ci' : "Aujourd'hui"}
        </button>
      )}
    </div>
  );
}
